// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface AdminUser {
  id: string;
  username: string;
  email: string;
  role: 'super_admin' | 'content_manager' | 'analyst' | 'support';
}

interface AuthState {
  token: string | null;
  user: AdminUser | null;
  isAuthenticated: boolean;
  login: (token: string, user: AdminUser) => void;
  logout: () => void;
}

export function isAdminTokenExpired(token: string): boolean {
  try {
    const [, payload] = token.split('.');
    if (!payload) return false;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='))) as {
      exp?: unknown;
      expiresAt?: unknown;
    };
    const now = Date.now();
    if (typeof json.exp === 'number' && json.exp * 1000 <= now) return true;
    if (typeof json.expiresAt === 'number' && json.expiresAt <= now) return true;
    return false;
  } catch {
    return false;
  }
}

const safeLocalStorage = {
  getItem: (name: string) => {
    try {
      const raw = localStorage.getItem(name);
      if (!raw) return null;
      try {
        JSON.parse(raw);
        return raw;
      } catch {
        localStorage.removeItem(name);
        return null;
      }
    } catch {
      return null;
    }
  },
  setItem: (name: string, value: string) => {
    try {
      localStorage.setItem(name, value);
    } catch {
      // ignore
    }
  },
  removeItem: (name: string) => {
    try {
      localStorage.removeItem(name);
    } catch {
      // ignore
    }
  },
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      login: (token, user) => set({ token, user, isAuthenticated: true }),
      logout: () => set({ token: null, user: null, isAuthenticated: false }),
    }),
    {
      name: 'summio-admin-auth',
      storage: createJSONStorage(() => safeLocalStorage),
      onRehydrateStorage: () => (state, error) => {
        if (error || (state?.token && isAdminTokenExpired(state.token))) {
          try {
            localStorage.removeItem('summio-admin-auth');
          } catch {
            // ignore
          }
          state?.logout();
        }
      },
    }
  )
);
